import { Popover, Portal, useDisclosure, VStack } from '@chakra-ui/react';
import { IQuoteRespSchema } from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { formatISO } from 'date-fns/formatISO';
import { isBefore } from 'date-fns/isBefore';
import { max } from 'date-fns/max';
import { parseISO } from 'date-fns/parseISO';
import isEmpty from 'lodash/isEmpty';
import { useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { MButton, MFlex, MFormField } from '../../../../../components/Monetize';
import { DatePicker } from '../../../../../components/Monetize/DatePicker/DatePicker';
import { FOCUS_WITHOUT_SCROLLING } from '../../../../../components/Monetize/Overlays';

function getQuickPickOptions() {
  return [15, 30, 60, 90].map((days) => {
    const newDate = addDays(new Date(), days);
    return {
      label: `${days} days - ${toDateShort(newDate, 'userTimezone')}`,
      value: formatISO(newDate, {
        representation: 'date',
      }),
    };
  });
}

interface QuoteUnExpireButtonProps {
  isActionLoading: boolean;
  quote: IQuoteRespSchema;
  onUnExpireQuote: (expirationDate: string) => void;
}

export const QuoteUnExpireButton = ({
  isActionLoading,
  quote,
  onUnExpireQuote,
}: QuoteUnExpireButtonProps) => {
  const { open: isOpen, onClose, onOpen } = useDisclosure();
  const firstElementRef = useRef(null);
  const [expirationDate, setExpirationDate] = useState(quote.expirationDate);
  const [minDate, setMinDate] = useState(() =>
    max([new Date(), parseISO(quote.expirationDate)]),
  );
  const [quickPickOptions, setQuickPickOptions] = useState(() =>
    getQuickPickOptions(),
  );

  const submitDisabled =
    isEmpty(expirationDate) || isBefore(expirationDate, minDate);

  const handleOpen = () => {
    setExpirationDate(quote.expirationDate);
    setMinDate(max([new Date(), parseISO(quote.expirationDate)]));
    setQuickPickOptions(getQuickPickOptions());
    onOpen();
  };

  return (
    <Popover.Root
      initialFocusEl={() => firstElementRef.current}
      open={isOpen}
      size="sm"
      variant="popover"
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
          handleOpen();
        } else {
          onClose();
        }
      }}
      positioning={{
        placement: 'bottom-end',
      }}
    >
      <Popover.Trigger asChild>
        <MButton
          mr={1.5}
          variant="secondary"
          data-testid="quote-unexpire-btn"
          isLoading={isActionLoading}
          isDisabled={isActionLoading}
        >
          Reactivate Quote
        </MButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            data-testid="extend-expiration-popover-content"
            minW="380px"
          >
            <Popover.Arrow />
            <Popover.Body>
              <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
                <MFormField label="New Expiration Date">
                  <DatePicker
                    value={expirationDate}
                    onChange={(date) => {
                      setExpirationDate(date ?? '');
                    }}
                    minDate={minDate}
                    btnProps={{ my: 0 }}
                  />
                </MFormField>
                <VStack gap={2} align="stretch" mt={2} mb={4}>
                  {quickPickOptions.map((option) => (
                    <MButton
                      key={option.value}
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setExpirationDate(option.value);
                      }}
                    >
                      {option.label}
                    </MButton>
                  ))}
                </VStack>
                <MFlex justifyContent="flex-end" w="100%">
                  <MButton
                    variant="primary"
                    isLoading={isActionLoading}
                    isDisabled={submitDisabled}
                    onClick={() => onUnExpireQuote(expirationDate)}
                  >
                    Save
                  </MButton>
                </MFlex>
              </FocusLock>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};
